import { Button, Checkbox, ComponentType, Grid, ModalBodyExtras, useModal } from '@gcl/react-core';
import React from 'react';
import { DISPLAY_DATE_TIME_FORMAT, convertISOStringToLocalDateString } from '@core/date.services';
import { IProjectLoadingRemovalCertCheckList } from './permitToLoadRemoveDetailPage.models';
import {
  ICheckListQuestion,
  permitToLoadRemovalCheckListQuestionMapper,
} from './permitToLoadRemovalCheckList.services';
import './permitToLoadRemovalCheckListDetailModal.scss';

export interface IUsePermitToLoadRemovalCheckListDetailModalProps {}

export interface IRenderPermitToLoadRemovalCheckListDetailModalProps {
  isSubmitLoading?: boolean;
  isSubmitError?: boolean;
  isSubmitSuccess?: boolean;
  checkListDetail: IProjectLoadingRemovalCertCheckList;
}

export const usePermitToLoadRemovalCheckListDetailModal = ({}: IUsePermitToLoadRemovalCheckListDetailModalProps) => {
  const { openPortal, closePortal: closeModal, renderModal } = useModal();
  const closePortal = React.useRef(closeModal);
  const renderPermitToLoadRemovalCheckListDetailModal = ({
    isSubmitLoading = false,
    isSubmitError = false,
    isSubmitSuccess = false,
    checkListDetail,
  }: IRenderPermitToLoadRemovalCheckListDetailModalProps) => {
    closePortal.current = () => {
      closeModal();
    };

    const questions = permitToLoadRemovalCheckListQuestionMapper(checkListDetail);

    return renderModal({
      large: true,
      title: `Checklist Detail`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        if (!checkListDetail || !questions) return <>Detail not found.</>;
        return (
          <Grid container flexWrap="wrap" className="gcl-checklist-modal-container">
            <Grid flex="0 0 100%" className="gcl-checklist-type gcl-ft-lg" item justifySelf="center" alignSelf="center">
              {checkListDetail.checkListType.name}
            </Grid>
            <Grid className="gcl-pb-sm" flex="0 0 100%" item>
              Inspected by: {checkListDetail.inspectedByProjectUserProfile?.userProfile?.displayName}
            </Grid>
            <Grid className="gcl-pb-sm" flex="0 0 100%" item>
              Datetime: {convertISOStringToLocalDateString(checkListDetail.inspectedDateTime, DISPLAY_DATE_TIME_FORMAT)}
            </Grid>
            <Grid className="gcl-pb-sm" flex="1 1 auto" item justifySelf="center" alignSelf="center">
              Structure:{' '}
              {checkListDetail.project_LoadingRemovalCertCheckListWorkType
                .map((s) => s.checkListWorkType.name)
                .join(', ')}
            </Grid>
            <Grid
              className="gcl-checklist-questions-container"
              container
              flex="1 1 auto"
              item
              justifySelf="center"
              alignSelf="center"
            >
              <Grid className="gcl-checklist-questions-header-row gcl-ft-md" container flexWrap="nowrap">
                <Grid className="gcl-checklist-questions-cell" flex="0 0 50%" item>
                  Checking Item
                </Grid>
                <Grid className="gcl-checklist-questions-cell" flex="0 0 25%" item>
                  Checked
                </Grid>
                <Grid className="gcl-checklist-questions-cell" flex="0 0 25%" item>
                  Value
                </Grid>
              </Grid>
              {Object.entries(questions).map(([key, value]: [key: string, value: ICheckListQuestion]) => {
                return value.type !== 'array' ? (
                  <Grid className="gcl-checklist-questions-row" container flexWrap="nowrap">
                    <Grid className="gcl-checklist-questions-cell" flex="0 0 50%" item>
                      {value.name}{' '}
                    </Grid>
                    <Grid container flexWrap="nowrap">
                      <Grid className="gcl-checklist-questions-cell" flex="0 0 50%" item>
                        <Checkbox
                          disabled
                          onChange={() => {}}
                          id={value.name as string}
                          checked={value.checkBoxFieldValue}
                        />
                      </Grid>
                      <Grid className="gcl-checklist-questions-cell" flex="0 0 50%" item>
                        {value.commentFieldValue}
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  value.questions?.map((question) => {
                    return (
                      <Grid className="gcl-checklist-questions-row" key={question.name} container flexWrap="nowrap">
                        <Grid className="gcl-checklist-questions-cell" flex="0 1 75%" item>
                          {question.name} {value.commentFieldValue}
                        </Grid>
                        {question?.type === 'dateTime' && (
                          <Grid className="gcl-checklist-questions-cell" flex="0 0 25%" item>
                            {convertISOStringToLocalDateString(question.commentFieldValue, DISPLAY_DATE_TIME_FORMAT)}
                          </Grid>
                        )}
                        {(question?.type === 'meter' || question.type === 'meterRate') && (
                          <Grid className="gcl-checklist-questions-cell" flex="0 0 25%" item>
                            {question.commentFieldValue}
                          </Grid>
                        )}
                        {question?.type === 'checkbox' && (
                          <Grid className="gcl-checklist-questions-cell" flex="1 0 50%" item>
                            <Checkbox
                              disabled
                              onChange={() => {}}
                              id={question.name as string}
                              checked={question.checkBoxFieldValue}
                            />
                          </Grid>
                        )}
                      </Grid>
                    );
                  })
                );
              })}
            </Grid>
          </Grid>
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <>
            <Button
              prefixIconName="ChevronLeft"
              type={ComponentType.Secondary}
              onClick={onHide}
              style={{ margin: '10px' }}
            >
              Close
            </Button>
          </>
        );
      },
    });
  };
  return {
    openPortal,
    closePortal: closePortal.current,
    renderModal: renderPermitToLoadRemovalCheckListDetailModal,
  };
};
